// Реестр ключей @capacitor/preferences.
//
// До реестра ключи были объявлены в шести файлах, и «выход человека» означал вспомнить
// все шесть. Забыть один — тривиально, а цена забытого несимметрична: оставленное меню
// предыдущего человека печатается следующим как своё, то есть с чужими сроками годности.
// Поэтому набор ключей объявлен ОДИН раз, а очистка ходит по объявлению, а не по памяти
// того, кто правит код.
//
// Ключ объявляет не только имя, но и ВРЕМЯ ЖИЗНИ: что принадлежит человеку — уходит
// с его выходом, что принадлежит устройству — выход переживает. Это прямое требование
// `offline-first.md` §3. Сессия на устройстве одна (решение Д3), поэтому выход стирает
// скоуп человека целиком: хранить его дальше не для кого.
//
// Чего здесь нет и не будет: очереди синхронизации. Она лежит в IndexedDB и не трогается
// ни при выходе, ни при отвязке — там неотправленные действия человека, и стереть их
// значит тихо потерять сделанную работу.

import { Preferences } from '@capacitor/preferences';

/** Кому принадлежит ключ — и, значит, когда он умирает. */
export type PreferenceLifetime =
  /** Человеку: стирается при выходе из системы. */
  | 'person'
  /** Устройству: переживает выход и уходит вместе с привязкой. */
  | 'device';

/** Пара, которой скоупятся данные: человек и его точка (`offline-first.md` §3). */
export type PreferenceScope = {
  readonly userId: string;
  readonly locationId: string;
};

export type PreferenceEntry = {
  /** Имя ключа целиком — либо его неизменная часть, если ключ достраивается скоупом. */
  readonly key: string;
  readonly lifetime: PreferenceLifetime;
  /**
   * Ключ достраивается парой (человек, точка) — см. `scopedPreferenceKey`.
   * Тогда `key` это префикс, а не готовый ключ.
   */
  readonly scoped: boolean;
  /**
   * У ключа есть копия в памяти модуля-владельца.
   *
   * Такой ключ стирает ТОЛЬКО владелец. Убрать его с диска мимо владельца значит
   * развести диск и память, а разошедшаяся пара хуже неубранного ключа: приложение
   * продолжит работать с токеном, которого на диске уже нет, и обнаружит это
   * при следующем запуске — то есть в самый неудобный момент.
   */
  readonly mirrored: boolean;
};

/**
 * Имена ключей. Владельцы импортируют их отсюда, а не объявляют у себя.
 *
 * Ключ, объявленный мимо реестра, для очистки не существует — именно так и появился
 * Р5: меню лежало под ключом, о котором выход не знал.
 */
export const PREFERENCE_KEYS = {
  /** Токен сессии человека. Владелец — `shared/auth/tokenStorage`. */
  sessionToken: 'restomarker.session.token',
  /** Режим отображения: кабинет или вид киоска. Владелец — `shared/storage/viewMode`. */
  viewMode: 'restomarker.device.viewMode',
  /** ПРЕФИКС кэша меню для печати: достраивается парой (человек, точка). */
  menu: 'restomarker.kiosk.menu',
  /** Привязка принтера. Владелец — `shared/printing/binding` (T-031). */
  printerBinding: 'restomarker.device.printerBinding',
  /** Адрес сервера сети. Владелец — `shared/api/serverAddress` (Фаза 4). */
  serverAddress: 'restomarker.device.serverAddress',
  /** Короткий тег устройства для суффикса FIFO-номера. Владелец — `shared/storage/deviceTag`. */
  deviceTag: 'restomarker.device.tag',
  /** Закрепление планшета за точкой (T-040). Владелец — `shared/auth/kioskBinding`. */
  kioskBinding: 'restomarker.device.kioskBinding',
  /** Когда на станции в последний раз подтвердили обход полок. Владелец — `shared/storage/shelfCheck`. */
  shelfCheckedAt: 'restomarker.device.shelfCheckedAt',
  /** Адрес этой установки в FCM (B-039). Владелец — `shared/push`. */
  pushAddress: 'restomarker.device.pushAddress',
  /**
   * Прежнее имя того же адреса (до R4). Владелец — `shared/push`: читает его один раз
   * и переносит в `pushAddress`. Удалить из реестра вместе с переходной веткой T-046.
   */
  legacyPushToken: 'restomarker.session.pushToken',
  /** Галочка «Уведомления Windows на этом устройстве» (T-047). Владелец — `feed/model/deviceMarks`. */
  alertsNotify: 'restomarker.device.alertsNotify',
  /** До какого сообщения лента точки просмотрена на этом устройстве, по точкам (T-047). */
  alertsSeen: 'restomarker.device.alertsSeen',
  /** До какого сообщения показаны системные уведомления, по точкам (T-047). */
  alertsNotified: 'restomarker.device.alertsNotified',
  /** Плашку «Включить уведомления Windows» над печатью скрыли на этом устройстве (№10). */
  alertsNudgeDismissed: 'restomarker.device.alertsNudgeDismissed',
} as const;

/**
 * Полный реестр. Появился новый ключ — строка здесь, иначе очистка его не увидит.
 *
 * Режим отображения помечен УСТРОЙСТВОМ, а не человеком, и это несущее решение: моноблок
 * на кухне настроен показывать киоск, и выход повара в конце смены не должен возвращать
 * его в кабинет. Иначе следующий, кто подойдёт, увидит экран аналитики вместо печати —
 * и первым делом полезет искать, как вернуть рабочий.
 */
export const PREFERENCE_REGISTRY: readonly PreferenceEntry[] = [
  { key: PREFERENCE_KEYS.sessionToken, lifetime: 'person', scoped: false, mirrored: true },
  { key: PREFERENCE_KEYS.viewMode, lifetime: 'device', scoped: false, mirrored: true },
  { key: PREFERENCE_KEYS.menu, lifetime: 'person', scoped: true, mirrored: false },
  // Принтер принадлежит СТАНЦИИ, не человеку (T-031): выход повара не должен требовать
  // от следующего настраивать принтер заново. `mirrored: true` — `binding.ts` держит
  // копию в памяти, чтобы `resolvePrinterAdapter` читал привязку синхронно на рендере.
  { key: PREFERENCE_KEYS.printerBinding, lifetime: 'device', scoped: false, mirrored: true },
  // Адрес сервера принадлежит СЕТИ и вводится один раз при настройке планшета:
  // выход сотрудника его не трогает. `mirrored: true` — `serverAddress.ts` держит
  // копию в памяти, httpClient читает базовый URL синхронно на каждый запрос.
  { key: PREFERENCE_KEYS.serverAddress, lifetime: 'device', scoped: false, mirrored: true },
  // Тег устройства принадлежит СТАНЦИИ и переживает пересменку: разводит FIFO-номера
  // двух устройств под одной учёткой (`deviceTag.ts`). `mirrored: true` — суффикс
  // строится синхронно в момент печати.
  { key: PREFERENCE_KEYS.deviceTag, lifetime: 'device', scoped: false, mirrored: true },
  // Закрепление планшета принадлежит СТАНЦИИ (T-040): выход повара на пересменке обязан
  // вернуть экран карточек, а не форму входа. `mirrored: false` — копии в памяти нет,
  // экран входа читает ключ асинхронно, и очищает его только владелец модуля.
  { key: PREFERENCE_KEYS.kioskBinding, lifetime: 'device', scoped: false, mirrored: false },
  // Обход полок принадлежит СТАНЦИИ, а не человеку, и это несущее решение: пересменка
  // не отменяет того, что холодильники обошли два часа назад. Принадлежи он человеку,
  // каждый заступивший повар получал бы требование обойти заново — и напоминание
  // превратилось бы в формальность, которую закрывают не глядя.
  { key: PREFERENCE_KEYS.shelfCheckedAt, lifetime: 'device', scoped: false, mirrored: false },
  // Адрес FCM принадлежит УСТАНОВКЕ, а не человеку (B-039). Выбор «присылать push» —
  // у человека на сервере, и адрес его не означает. Раньше ключ жил временем человека,
  // и галочка, нарисованная по его наличию, слетала при каждом «Выйти»: вход ничего
  // не восстанавливал. Выход адрес переживает — следующий вход подключит его к своей
  // сессии или снимет с прежнего владельца (`sync`), а без него снимать было бы нечего.
  { key: PREFERENCE_KEYS.pushAddress, lifetime: 'device', scoped: false, mirrored: false },
  // Прежнее имя — тоже устройство: выход до первого чтения новым бандлом не должен
  // унести адрес, который ещё не перенесли.
  { key: PREFERENCE_KEYS.legacyPushToken, lifetime: 'device', scoped: false, mirrored: false },
  // Лента точки (T-047): «прочитано» и галочка уведомлений — УСТРОЙСТВА, не человека
  // (решение владельца 2026-09-29, урок B-039: ключ с временем жизни `person` стирался при
  // выходе, и галочка «слетала»). Пересменка их не трогает. `mirrored: true` — копия
  // в памяти `deviceMarks.ts`: счётчик на колокольчике считается синхронно на рендере.
  { key: PREFERENCE_KEYS.alertsNotify, lifetime: 'device', scoped: false, mirrored: true },
  { key: PREFERENCE_KEYS.alertsSeen, lifetime: 'device', scoped: false, mirrored: true },
  { key: PREFERENCE_KEYS.alertsNotified, lifetime: 'device', scoped: false, mirrored: true },
  { key: PREFERENCE_KEYS.alertsNudgeDismissed, lifetime: 'device', scoped: false, mirrored: true },
];
// Счётчик FIFO (`restomarker.kiosk.fifoCounter`) удалён в T-036: номер выводится
// из локальных записей о печати, отдельного ключа больше нет.

/**
 * Ключ в скоупе пары (человек, точка).
 *
 * Человек стоит ПЕРЕД точкой не для красоты. Выход чистит скоуп человека целиком,
 * включая все его точки — а их у сотрудника может быть несколько, — и такой порядок
 * делает «всё моё» одним префиксом. Обратный порядок потребовал бы перебирать точки,
 * список которых клиенту офлайн взять неоткуда.
 */
export function scopedPreferenceKey(prefix: string, scope: PreferenceScope): string {
  return `${prefix}.${scope.userId}.${scope.locationId}`;
}

/**
 * Стереть ключи, принадлежащие человеку. `null` — человек неизвестен.
 *
 * Неизвестный человек чистится ЗА ВСЕХ, и это осознанный выбор по несимметричной цене:
 * оставленный свой скоуп — это меню чужой точки, напечатанное как своё (Р5, класс
 * дефекта «сроки годности»), а стёртый чужой скоуп — один поход коллеги в сеть.
 *
 * Ключи с копией в памяти сюда не попадают: их стирает владелец, см. `PreferenceEntry`.
 */
export async function clearPersonPreferences(userId: string | null): Promise<void> {
  const { keys } = await Preferences.keys();

  await Promise.all(
    keys.filter((key) => belongsToPerson(key, userId)).map((key) => Preferences.remove({ key })),
  );
}

function belongsToPerson(key: string, userId: string | null): boolean {
  return PREFERENCE_REGISTRY.some((entry) => {
    if (entry.lifetime !== 'person' || entry.mirrored) {
      return false;
    }
    if (!entry.scoped) {
      return key === entry.key;
    }

    // Точка после префикса обязательна: без неё `…menu` совпал бы с любым ключом,
    // который начинается так же, — и очистка меню однажды унесла бы соседний кэш.
    const prefix = userId === null ? `${entry.key}.` : `${entry.key}.${userId}.`;
    return key.startsWith(prefix);
  });
}
